/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/globals */
/**
 * UiCustomizationContext — Customer branding and dynamic dropdown dictionaries.
 *
 * Fetched once at app startup from GET /api/v1/customer/branding.
 * The endpoint is intentionally unauthenticated so the login page can display
 * the correct company logo and default language before the user signs in.
 *
 * Configuration source (backend priority order):
 *   1. UI_CUSTOMIZATION_CONFIG_PATH env var  → dedicated override YAML
 *   2. CUSTOMER_CONFIG (or CUSTOMER_ID) env var → config/ui_customization/{key}.yaml
 *   3. Schema defaults
 *
 * Usage:
 *   const { branding, dictionaries } = useUiCustomization()
 *   const categories = useStoreCategories()
 *   const industries = useIndustries()
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from 'react'
import { logger } from '../utils/logger'
import {
  fetchUiCustomization,
  UiCustomizationRequestError,
  type UiBranding,
  type UiDictionaries,
  type UiCustomization,
  type StoreCategoryItem,
  type IndustryItem,
  type OntologyFamilyItem,
  type DeploymentEnvironmentItem,
} from '../services/uiCustomizationApi'
import { BRANDING_SETTINGS_CHANGED_EVENT } from '../services/settingsApi'

// ---------------------------------------------------------------------------
// Defaults — mirror backend Pydantic defaults so the UI never breaks if the
// endpoint is slow or unavailable during first render.
// ---------------------------------------------------------------------------

/**
 * Brand shown when the deployment configures no custom company name — and the
 * ONLY value that may be rendered as a brand before the branding response has
 * landed is nothing at all. Showing this to a white-labelled customer (e.g.
 * GERNAS) leaks the vendor brand, so consumers must gate on `isResolved` from
 * `useBrandName()` rather than rendering the fallback while loading.
 */
export const FALLBACK_BRAND_NAME = 'AQUA'

/**
 * Bundled product favicon (`public/vite.svg`). Applied only for Atom.ai when
 * branding has resolved — never while the request is in flight / failed, so
 * white-labelled tabs never flash it.
 */
export const DEFAULT_FAVICON_URL = '/vite.svg'

/**
 * Config values that mean "no custom brand": the Pydantic schema default
 * ('TAGP'), the pre-fetch placeholder (''), and the vendor brand itself.
 */
const PLACEHOLDER_BRAND_NAMES = new Set(['', 'TAGP', FALLBACK_BRAND_NAME])

export function resolveFaviconHref(
  companyName: string | null | undefined,
  companyLogoUrl: string | null | undefined,
): string | null {
  const trimmed = (companyName ?? '').trim()
  if (trimmed.toLowerCase() === 'atom.ai') return companyLogoUrl || DEFAULT_FAVICON_URL
  return companyLogoUrl || null
}

/** Single source of truth for company_name → wordmark resolution. */
export function resolveBrandName(companyName: string | null | undefined): string {
  const name = (companyName ?? '').trim()
  return PLACEHOLDER_BRAND_NAMES.has(name) ? FALLBACK_BRAND_NAME : name
}

/**
 * Single source of truth for the wordmark shown before the user signs in (login
 * page, registration and signup modals). A deployment can launch under a
 * product or programme name that differs from the company name used inside the
 * app; `branding.launch_name` carries that label.
 *
 * Missing, null or blank all fall back to the resolved company name — the key
 * is absent from most deployment YAMLs and from any backend that predates the
 * field, and neither should change what the login page paints.
 */
export function resolveLaunchName(
  launchName: string | null | undefined,
  companyName: string | null | undefined,
): string {
  const name = (launchName ?? '').trim()
  return name || resolveBrandName(companyName)
}

// ---------------------------------------------------------------------------
// Brand token — for product copy that names the platform
//
// Help text, node descriptions, tooltips and confirmations used to hardcode the
// internal codename ("registered in TAGP"), which reads as the vendor's name to
// a white-labelled customer. Those strings now carry BRAND_TOKEN and are
// resolved to the deployment's company_name at display time.
//
// Most of that copy lives in plain data modules (help catalogs, node
// constants) that are evaluated at import time, long before branding is
// fetched — so substitution must happen when the text is read, not when it is
// defined. React call sites use useBrandedText(); the non-React chokepoints
// that serve those catalogs use applyBrand().
// ---------------------------------------------------------------------------

/** Placeholder for the deployment's brand inside product copy. */
export const BRAND_TOKEN = '{brand}'

// Module-level snapshot of the resolved brand, kept current by the provider.
// Static catalogs are read from plain functions with no access to context; by
// the time any of that copy is on screen the branding request has long since
// settled, so a snapshot is sufficient and avoids threading context through
// every help lookup.
let currentBrandName: string = FALLBACK_BRAND_NAME

/**
 * Resolved brand for non-React callers (help catalogs, formatters).
 * Prefer useBrandName() inside components — this cannot trigger a re-render.
 */
export function getBrandName(): string {
  return currentBrandName
}

/** Replace every BRAND_TOKEN in `text` with the deployment's brand. */
export function applyBrand(text: string | null | undefined, brand: string = currentBrandName): string {
  if (typeof text !== 'string') return ''
  return text.includes(BRAND_TOKEN) ? text.split(BRAND_TOKEN).join(brand) : text
}

/**
 * applyBrand over every string in a nested structure, for the help/info
 * catalogs that hold copy in arrays and objects rather than flat fields.
 * Returns the input unchanged when it contains no token, so memoised consumers
 * keep referential equality.
 */
export function applyBrandDeep<T>(value: T, brand: string = currentBrandName): T {
  if (typeof value === 'string') return applyBrand(value, brand) as T
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((v) => {
      const branded = applyBrandDeep(v, brand)
      if (branded !== v) changed = true
      return branded
    })
    return (changed ? next : value) as T
  }
  if (value && typeof value === 'object') {
    let changed = false
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      const branded = applyBrandDeep(v, brand)
      if (branded !== v) changed = true
      out[k] = branded
    }
    return (changed ? out : value) as T
  }
  return value
}

const DEFAULT_BRANDING: UiBranding = {
  // Empty (not 'TAGP') so a consumer that forgets to gate on `isResolved`
  // renders nothing rather than the wrong company's brand.
  company_name: '',
  launch_name: null,
  company_logo_url: null,
  logo_text_gradient: null,
  theme_name: null,
  language: 'en',
  supported_languages: ['en', 'zh', 'ja', 'de', 'fr', 'ar'],
  platform_only: false,
  hide_platform_suffix: null,
  support_email: null,
  show_product_docs: true,
}

const DEFAULT_DICTIONARIES: UiDictionaries = {
  store_categories: [],
  industries: [],
  ontology_families: [],
  deployment_environments: [],
}

const DEFAULT_UI_CUSTOMIZATION: UiCustomization = {
  branding: DEFAULT_BRANDING,
  ui_dictionaries: DEFAULT_DICTIONARIES,
}

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

export interface UiCustomizationContextValue {
  branding: UiBranding
  dictionaries: UiDictionaries
  isLoading: boolean
  /**
   * True once the branding request has settled (success or failure). Until then
   * `branding` holds placeholder defaults that must not be rendered as a brand.
   */
  isResolved: boolean
  error: Error | null
  refresh: () => Promise<void>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const UiCustomizationContext = createContext<UiCustomizationContextValue>({
  branding: DEFAULT_BRANDING,
  dictionaries: DEFAULT_DICTIONARIES,
  isLoading: true,
  isResolved: false,
  error: null,
  refresh: async () => {},
})

// ---------------------------------------------------------------------------
// Retry
//
// Branding is fetched once per page load and nothing re-requests it, so a
// single transient failure used to pin the fallback brand for the whole
// session — which is what users on slow or flaky connections were seeing.
// A few backed-off attempts let the tab recover on its own.
// ---------------------------------------------------------------------------

/** Backoff before attempts 2..4. Its length is also the retry budget. */
const RETRY_DELAYS_MS = [500, 2000, 5000]

/** Abortable sleep: resolves early (without throwing) once `signal` aborts. */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })
}

/**
 * Whether another attempt could plausibly succeed. A bare network error (no
 * status — the slow/dropped-connection case) and 5xx/408/429 are transient;
 * every other 4xx is the deployment's answer and will not change on a retry.
 */
function isRetryable(err: unknown): boolean {
  if (!(err instanceof UiCustomizationRequestError)) return true
  const { status } = err
  return status >= 500 || status === 408 || status === 429
}

function isStaleUiCustomizationRequest(
  requestId: number,
  requestIdRef: React.MutableRefObject<number>,
): boolean {
  return requestId !== requestIdRef.current
}

async function fetchUiCustomizationWithRetries(
  controller: AbortController,
  requestId: number,
  requestIdRef: React.MutableRefObject<number>,
  onSuccess: (result: UiCustomization) => void,
  onFailure: (error: Error) => void,
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      const result = await fetchUiCustomization(controller.signal)
      if (isStaleUiCustomizationRequest(requestId, requestIdRef)) return
      onSuccess(result)
      return
    } catch (err) {
      if (controller.signal.aborted || isStaleUiCustomizationRequest(requestId, requestIdRef)) return

      if (attempt < RETRY_DELAYS_MS.length && isRetryable(err)) {
        logger.warn('UI customization request failed, retrying', err, {
          attempt: attempt + 1,
          delayMs: RETRY_DELAYS_MS[attempt],
        })
        await delay(RETRY_DELAYS_MS[attempt], controller.signal)
        if (controller.signal.aborted || isStaleUiCustomizationRequest(requestId, requestIdRef)) return
        continue
      }

      const e = err instanceof Error ? err : new Error('Failed to load UI customization')
      onFailure(e)
      return
    }
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const UiCustomizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<UiCustomization>(DEFAULT_UI_CUSTOMIZATION)
  const [isLoading, setIsLoading] = useState(true)
  const [isResolved, setIsResolved] = useState(false)
  // Distinct from isResolved: true only once branding has actually been
  // fetched. isResolved also flips on failure (so in-app consumers can stop
  // rendering placeholders), but the browser tab must not adopt a brand the
  // deployment never sent us — see the title and favicon effects below.
  const [hasBranding, setHasBranding] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Request sequencing: only the newest in-flight request may write state, so a
  // slow initial response can never overwrite a fast refresh() that followed it.
  const requestIdRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)

  const load = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const requestId = ++requestIdRef.current

    setIsLoading(true)
    setError(null)

    await fetchUiCustomizationWithRetries(
      controller,
      requestId,
      requestIdRef,
      (result) => {
        setData(result)
        setHasBranding(true)
        logger.info('UI customization loaded', {
          company: result.branding.company_name,
          language: result.branding.language,
          categories: result.ui_dictionaries.store_categories.length,
          industries: result.ui_dictionaries.industries.length,
        })
      },
      (e) => {
        logger.error('Failed to load UI customization, using defaults', e)
        setError(e)
      },
    )

    if (requestId === requestIdRef.current) {
      setIsLoading(false)
      // Settled either way — in-app consumers may now render the resolved
      // brand (the customer's name on success, the bundled fallback on
      // failure). The tab keys off hasBranding instead.
      setIsResolved(true)
    }
  }, [])

  useEffect(() => {
    void load()
    return () => abortRef.current?.abort()
  }, [load])

  // Platform/tenant admins persist branding via a different API; notify via
  // BRANDING_SETTINGS_CHANGED_EVENT so About (support_email) and dictionaries
  // pick up changes without a full page reload.
  useEffect(() => {
    const onBrandingSettingsChanged = () => {
      void load()
    }
    window.addEventListener(BRANDING_SETTINGS_CHANGED_EVENT, onBrandingSettingsChanged)
    return () =>
      window.removeEventListener(BRANDING_SETTINGS_CHANGED_EVENT, onBrandingSettingsChanged)
  }, [load])

  // Keep the module-level snapshot in step with context, so applyBrand() and
  // getBrandName() serve the same brand to non-React callers. Assigned during
  // render (not in an effect) because a help catalog can be read by a child's
  // render pass before any effect has run.
  currentBrandName = isResolved
    ? resolveBrandName(data.branding.company_name)
    : FALLBACK_BRAND_NAME

  // Drive the browser tab title and SEO/social meta tags from the customer's
  // configured company name, via the shared resolveBrandName() convention.
  //
  // Gated on hasBranding, not isResolved: index.html ships a brand-neutral
  // title, and writing a brand we never received would put the vendor wordmark
  // in a white-labelled deployment's tab — before the response lands, and for
  // the rest of the session if the request ultimately fails.
  useEffect(() => {
    if (!hasBranding) return
    const brand = resolveBrandName(data.branding.company_name)

    if (document.title !== brand) {
      document.title = brand
    }

    const setMeta = (selector: string, content: string) => {
      const el = document.head.querySelector<HTMLMetaElement>(selector)
      if (el && el.getAttribute('content') !== content) {
        el.setAttribute('content', content)
      }
    }

    const socialTitle = `${brand} - Enterprise AI Agents Platform`
    setMeta('meta[property="og:title"]', socialTitle)
    setMeta('meta[name="twitter:title"]', socialTitle)
    setMeta(
      'meta[name="description"]',
      `${brand} - Enterprise AI Agents & Orchestration Platform. Seamlessly build, monitor, and scale production-ready multi-agent workflows.`,
    )
  }, [data.branding.company_name, hasBranding])

// Swap the browser tab favicon once branding resolves.
  // Atom.ai → /vite.svg; otherwise company_logo_url from API; else keep blank.
    useEffect(() => {
    if (!hasBranding) return

    const href = resolveFaviconHref(data.branding.company_name, data.branding.company_logo_url)
    if (!href) return

    // Replace rather than mutate: browsers do not reliably repaint the tab icon
    // when an existing <link rel="icon"> has its href changed in place.
    document.head.querySelectorAll('link[rel="icon"]').forEach((el) => el.remove())
    const link = document.createElement('link')
    link.rel = 'icon'
    // No `type` — let the browser infer it, since the customer logo may be any
    // image format while the bundled default is SVG.
    link.setAttribute('href', href)
    document.head.appendChild(link)
  }, [data.branding.company_logo_url, data.branding.company_name, hasBranding])

  const contextValue = useMemo<UiCustomizationContextValue>(
    () => ({
      branding: data.branding,
      dictionaries: data.ui_dictionaries,
      isLoading,
      isResolved,
      error,
      refresh: load,
    }),
    [data, isLoading, isResolved, error, load],
  )

  return (
    <UiCustomizationContext.Provider value={contextValue}>
      {children}
    </UiCustomizationContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Primary hook
// ---------------------------------------------------------------------------

export function useUiCustomization(): UiCustomizationContextValue {
  return useContext(UiCustomizationContext)
}

// ---------------------------------------------------------------------------
// Convenience selector hooks
// ---------------------------------------------------------------------------

/** Company branding: logo URL, primary colour, name. */
export function useBranding(): UiBranding {
  return useUiCustomization().branding
}

/**
 * Whether the "Platform" suffix is dropped from the brand wordmark.
 *
 * `branding.hide_platform_suffix` decides it on its own; when it is unset
 * (null/undefined — an older backend, or a deployment that never configured
 * the key) it inherits `branding.platform_only`, which is how the two used to
 * be coupled. Use this instead of reading `platform_only` for suffix
 * decisions, so a deployment can hide the suffix without locking the app to
 * the Platform view — or keep the suffix while locked.
 */
export function isPlatformSuffixHidden(branding: UiBranding): boolean {
  return branding.hide_platform_suffix ?? branding.platform_only
}

/** Hook form of isPlatformSuffixHidden() for components already in context. */
export function usePlatformSuffixHidden(): boolean {
  return isPlatformSuffixHidden(useBranding())
}

export interface BrandName {
  /** Wordmark to render: the customer's company_name, else the AQUA fallback. */
  name: string
  /**
   * False until the branding request settles. Callers MUST render a neutral
   * placeholder (not `name`) while false — otherwise a white-labelled
   * deployment flashes the vendor brand for the length of the request, which
   * is exactly what users on slow connections were reporting.
   */
  isResolved: boolean
}

/**
 * Resolved brand wordmark + whether it is safe to display yet.
 *
 * Single source of truth for the "company_name → wordmark" rule that was
 * previously copy-pasted across the sidebar, brand logo, breadcrumbs, Ideate
 * hero and blueprint wizard.
 */
export function useBrandName(): BrandName {
  const { branding, isResolved } = useUiCustomization()
  return useMemo(
    () => ({ name: resolveBrandName(branding.company_name), isResolved }),
    [branding.company_name, isResolved],
  )
}

/**
 * Wordmark for the pre-authentication screens — the login page and the
 * registration/signup modals — resolved from `branding.launch_name`.
 *
 * Those screens name the deployment's launch brand rather than the company
 * brand the signed-in app carries, so they must NOT use useBrandName(). Same
 * `isResolved` contract: render nothing branded until it is true.
 */
export function useLaunchName(): BrandName {
  const { branding, isResolved } = useUiCustomization()
  return useMemo(
    () => ({
      name: resolveLaunchName(branding.launch_name, branding.company_name),
      isResolved,
    }),
    [branding.launch_name, branding.company_name, isResolved],
  )
}

/**
 * Substitutes BRAND_TOKEN in product copy with the deployment's brand, and
 * re-runs when branding resolves — unlike the bare applyBrand(), which reads a
 * snapshot and cannot re-render. Use this for anything shown in the UI.
 *
 *   const brandText = useBrandedText()
 *   <p>{brandText('Cloud storage folder registered in {brand}.')}</p>
 */
export function useBrandedText(): (text: string) => string {
  const { name } = useBrandName()
  return useCallback((text: string) => applyBrand(text, name), [name])
}

/** Agent Store and Workflow Store category filter chips. */
export function useStoreCategories(): StoreCategoryItem[] {
  return useUiCustomization().dictionaries.store_categories
}

/** Industries shared across Blueprints, Ideate, EO Data Models, and KPI Library. */
export function useIndustries(): IndustryItem[] {
  return useUiCustomization().dictionaries.industries
}

/** KPI families for KPI Library and Ontology Canvas. */
export function useOntologyFamilies(): OntologyFamilyItem[] {
  return useUiCustomization().dictionaries.ontology_families
}

/** Deployment environment badge options (DEV, QA, PROD, …). */
export function useDeploymentEnvironments(): DeploymentEnvironmentItem[] {
  return useUiCustomization().dictionaries.deployment_environments
}

/** Platform support contact email from customer config (null when not configured). */
export function useSupportEmail(): string | null {
  return useUiCustomization().branding.support_email
}

/**
 * Whether the product documentation link is shown (branding.show_product_docs).
 * Defaults to true so a backend that predates the flag keeps the link visible —
 * the response replaces DEFAULT_BRANDING wholesale, so the field can be absent.
 */
export function useShowProductDocs(): boolean {
  return useUiCustomization().branding.show_product_docs !== false
}

/** Default language code from customer config. */
export function useDefaultLanguage(): string {
  return useUiCustomization().branding.language
}

/** Supported language codes from customer config. */
export function useSupportedLanguages(): string[] {
  return useUiCustomization().branding.supported_languages
}

export type {
  StoreCategoryItem,
  IndustryItem,
  OntologyFamilyItem,
  DeploymentEnvironmentItem,
  UiBranding,
  UiDictionaries,
  UiCustomization,
}

export default UiCustomizationContext
